import React, { useEffect, useRef } from 'react';

const InteractiveBackground = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const particleCount = 80; // More stars for starry sky effect

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle class
    class Particle {
      constructor() {
        this.reset();
        this.y = Math.random() * canvas.height;
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = Math.random() * 0.3 - 0.15;
        this.speedY = Math.random() * 0.3 - 0.15;
        this.baseOpacity = Math.random() * 0.4 + 0.6; // Base opacity for twinkling (0.6-1.0)
        this.opacity = this.baseOpacity;
        this.twinkleSpeed = Math.random() * 0.02 + 0.01;
        this.twinklePhase = Math.random() * Math.PI * 2;
        this.color = this.getRandomColor();
      }

      getRandomColor() {
        const colors = [
          { r: 214, g: 187, b: 251 }, // Purple 100
          { r: 127, g: 86, b: 217 },  // Purple 200
          { r: 105, g: 65, b: 198 },  // Purple 300
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Reset if off screen
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;

        // Twinkle effect for stars
        this.twinklePhase += this.twinkleSpeed;
        const twinkle = (Math.sin(this.twinklePhase) + 1) * 0.3 + 0.7; // Oscillate between 0.7 and 1.0

        // Mouse interaction - stars react slightly
        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 100) {
          const angle = Math.atan2(dy, dx);
          const force = (100 - distance) / 100;
          this.x -= Math.cos(angle) * force * 1.5;
          this.y -= Math.sin(angle) * force * 1.5;
          this.opacity = Math.min(1, this.baseOpacity * twinkle + 0.2);
        } else {
          this.opacity = this.baseOpacity * twinkle;
        }
      }

      draw() {
        // Draw star glow
        const glowGradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 4);
        glowGradient.addColorStop(0, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.8})`);
        glowGradient.addColorStop(0.5, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.3})`);
        glowGradient.addColorStop(1, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0)`);
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();

        // Draw bright star core
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    // Create more visible stars
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(new Particle());
    }

    // Meteorite trail that follows mouse
    const meteorTrail = {
      points: [],
      maxLength: 30,
      lastMouseX: 0,
      lastMouseY: 0,
    };

    // Mouse move handler - create continuous meteor trail
    const handleMouseMove = (e) => {
      const currentX = e.clientX;
      const currentY = e.clientY;
      
      // Calculate distance from last point
      const dx = currentX - meteorTrail.lastMouseX;
      const dy = currentY - meteorTrail.lastMouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Add point to trail if mouse has moved enough
      if (distance > 3) {
        meteorTrail.points.push({
          x: currentX,
          y: currentY,
          timestamp: Date.now(),
        });

        // Limit trail length
        if (meteorTrail.points.length > meteorTrail.maxLength) {
          meteorTrail.points.shift();
        }

        meteorTrail.lastMouseX = currentX;
        meteorTrail.lastMouseY = currentY;
      }

      mouseRef.current.x = currentX;
      mouseRef.current.y = currentY;
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesRef.current.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      // Draw connections between nearby particles
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const dx = particlesRef.current[i].x - particlesRef.current[j].x;
          const dy = particlesRef.current[i].y - particlesRef.current[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            const opacity = (1 - distance / 120) * 0.2;
            ctx.beginPath();
            ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
            ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
            ctx.strokeStyle = `rgba(214, 187, 251, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      // Draw meteor trail that follows mouse
      if (meteorTrail.points.length > 1) {
        const now = Date.now();
        const maxAge = 500; // Trail points fade after 500ms

        ctx.save();

        // Draw trail with gradient
        for (let i = 0; i < meteorTrail.points.length - 1; i++) {
          const point1 = meteorTrail.points[i];
          const point2 = meteorTrail.points[i + 1];
          
          // Calculate age and opacity
          const age = now - point1.timestamp;
          const opacity = Math.max(0, 1 - (age / maxAge));
          
          if (opacity <= 0) continue;

          // Calculate progress through trail (0 = tail, 1 = head)
          const progress = i / meteorTrail.points.length;
          
          // Create gradient for this segment
          const gradient = ctx.createLinearGradient(
            point1.x, point1.y,
            point2.x, point2.y
          );
          
          // Colors based on position in trail
          const tailColor = { r: 214, g: 187, b: 251 }; // Purple 100
          const headColor = { r: 255, g: 255, b: 255 }; // White
          
          const r = Math.round(tailColor.r + (headColor.r - tailColor.r) * progress);
          const g = Math.round(tailColor.g + (headColor.g - tailColor.g) * progress);
          const b = Math.round(tailColor.b + (headColor.b - tailColor.b) * progress);
          
          gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${opacity * 0.3})`);
          gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${opacity * 0.8})`);
          
          ctx.beginPath();
          ctx.moveTo(point1.x, point1.y);
          ctx.lineTo(point2.x, point2.y);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 4 + progress * 2; // Thicker at head
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        // Draw bright head at current mouse position
        if (meteorTrail.points.length > 0) {
          const headPoint = meteorTrail.points[meteorTrail.points.length - 1];
          
          // Outer glow
          const glowGradient = ctx.createRadialGradient(
            headPoint.x, headPoint.y, 0,
            headPoint.x, headPoint.y, 15
          );
          glowGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
          glowGradient.addColorStop(0.4, 'rgba(214, 187, 251, 0.7)');
          glowGradient.addColorStop(1, 'rgba(214, 187, 251, 0)');
          
          ctx.beginPath();
          ctx.arc(headPoint.x, headPoint.y, 12, 0, Math.PI * 2);
          ctx.fillStyle = glowGradient;
          ctx.fill();

          // Bright white core
          ctx.beginPath();
          ctx.arc(headPoint.x, headPoint.y, 4, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255, 255, 255, 1)';
          ctx.fill();
        }

        ctx.restore();

        // Remove old trail points
        meteorTrail.points = meteorTrail.points.filter(p => (now - p.timestamp) < maxAge);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener('mousemove', handleMouseMove);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ background: '#111216' }}
    />
  );
};

export default InteractiveBackground;
